/**
 * HTTP client with response extraction — shared by V2 UI and export paths that need the same behavior as V1 proxy.
 */
import axios from "axios";
import { userLogout } from "../utils/common";
import Config from "../utils/config";
import { extractResponse } from "./apiExtractor";

/** Same host as utils/Request.js so V2 pages call the same API as V1 (not only the CRA proxy). */
// let apiBasePath = "https://uat-apps.mufinpay.com";
let apiBasePath = ""
let cachedInstance = null;

function createInstance() {
  const headers = {
    "X-Frame-Options": "SAMEORIGIN",
    "Content-Security-Policy": "default-src 'self'",
    "Strict-Transport-Security": "max-age=31536000",
    "X-Content-Type-Options": "nosniff",
    "Referrer-Policy": "no-referrer",
    "Permissions-Policy": "geolocation=()",
  };

  const auth = sessionStorage.getItem("authToken");
  if (auth) {
    headers.Authorization = auth;
  }

  const instance = axios.create({
    baseURL: "",
    headers,
  });

  instance.interceptors.request.use((config) => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      if (response?.config?.skipExtractor) {
        return response;
      }
      try {
        const extracted = extractResponse(response.data);
        response.data = extracted;
      } catch (err) {
        if (err.isBusinessError) {
          return Promise.reject(err);
        }
        throw err;
      }
      return response;
    },
    (error) => {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 401) {
        userLogout();
        window.location.href = "/admin";
      }

      const normalizedError = {
        ...error,
        response: {
          ...error?.response,
          data: data ?? error?.response?.data,
          status: status ?? error?.response?.status,
        },
        message: data?.message ?? data?.msg ?? error?.message,
      };

      return Promise.reject(normalizedError);
    }
  );

  return instance;
}

class RequestV2 {
  constructor(dispatch, successFn, errorFn) {
    this.successFn = typeof successFn === "function" ? successFn : () => {};
    this.errorFn = typeof errorFn === "function" ? errorFn : () => {};
    this.dispatch = typeof dispatch === "function" ? dispatch : () => {};
  }

  instance() {
    if (!cachedInstance) {
      cachedInstance = createInstance();
    }
    return cachedInstance;
  }

  async get(url, params = {}) {
    try {
      const res = await this.instance().get(apiBasePath + url, params);
      const data = res.data ?? null;
      const headers = res.headers ?? null;
      this.successFn(data, headers);
      return res;
    } catch (error) {
      this.errorFn(error);
    }
  }

  async post(url, params = {}) {
    try {
      const res = await this.instance().post(apiBasePath + url, params);
      const { data = {}, status } = res;
      this.successFn(data, status);
      return res;
    } catch (error) {
      this.errorFn({
        response: error?.response,
        message: error?.message,
      });
    }
  }

  async put(url, params = {}) {
    try {
      const res = await this.instance().put(apiBasePath + url, params);
      const { data = {}, status } = res;
      this.successFn(data, status);
      return res;
    } catch (error) {
      if (
        url.startsWith(Config.apis.user.UPDATE_USER_MCC_CODE) &&
        error?.response?.status == "404"
      ) {
        this.errorFn(error.response.data);
        return;
      }
      this.errorFn({
        response: error?.response,
        message: error?.message,
      });
    }
  }

  async delete(url, params = {}) {
    try {
      const res = await this.instance().delete(apiBasePath + url, params);
      const { data = {}, status } = res;
      this.successFn(data, status);
      return res;
    } catch (error) {
      this.errorFn({
        response: error?.response,
        message: error?.message,
      });
    }
  }
}

export default RequestV2;
